import axios from 'axios';
import { requestNotificationPermission, onMessageListener } from '../config/firebase';
import { API_URL } from '../config/api';

/**
 * Initialize push notifications for the current user
 * This should be called after user logs in
 */
export const initializePushNotifications = async (token: string): Promise<void> => {
  try {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      return;
    }

    // Check if service worker is supported
    if (!('serviceWorker' in navigator)) {
      return;
    }

    // Register service worker
    try {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
    } catch (error) {
      console.error('Service worker registration failed:', error);
      return;
    }

    // Request notification permission and get FCM token
    const deviceToken = await requestNotificationPermission();

    if (!deviceToken) {
      // Silently skip if no device token (Firebase not configured)
      return;
    }

    // Register device token with backend
    try {
      await axios.post(
        `${API_URL}/api/notifications/register-token`,
        { deviceToken },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error: any) {
      console.error('Failed to register device token:', error.response?.data || error.message);
    }

    // Listen for foreground messages
    onMessageListener()
      .then((payload: any) => {
        console.log('📨 Foreground message received:', payload);

        // Show notification if the app is in foreground
        if (Notification.permission === 'granted') {
          new Notification(payload.notification?.title || 'התראה חדשה', {
            body: payload.notification?.body || '',
            icon: payload.notification?.icon || '/matat-logo.svg',
            badge: '/matat-logo.svg',
            data: payload.data
          });
        }
      })
      .catch((error) => {
        console.error('Error listening for messages:', error);
      });

  } catch (error) {
    console.error('❌ Error initializing push notifications:', error);
  }
};

/**
 * Unregister device token when user logs out
 */
export const unregisterDeviceToken = async (token: string, deviceToken: string): Promise<void> => {
  try {
    await axios.delete(
      `${API_URL}/api/notifications/remove-token`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        data: { deviceToken }
      }
    );

    console.log('✅ Device token unregistered');
  } catch (error: any) {
    console.error('❌ Failed to unregister device token:', error.response?.data || error.message);
  }
};
